// SEP 911 preescolar/primaria/secundaria full form preview — mimics official layout.
// Renders header, identification block, and ALL sections (I–VIII + oficialización)
// aggregated from /api/reportes/plantel/... backend endpoint.
import { Fragment } from "react";
import { NIVEL_EDUCATIVO_911, FUNCION_PERSONAL_911 } from "@/lib/sepCatalogos";

// Add synthetic "DOCENTE_FRENTE_GRUPO" so its label is available in the matrix.
const FUNCION_LABEL_MAP = {
  DOCENTE_FRENTE_GRUPO: "Docente frente a grupo",
  DIRECTOR_CON_GRUPO: "Director con grupo",
  DIRECTOR_SIN_GRUPO: "Director sin grupo",
  ...Object.fromEntries(FUNCION_PERSONAL_911.map((f) => [f.value, f.label])),
};
const NIVEL_LABEL_MAP = Object.fromEntries(NIVEL_EDUCATIVO_911.map((n) => [n.value, n.label]));

const NIVEL_LABEL = {
  PREESCOLAR: "EDUCACIÓN PREESCOLAR",
  PRIMARIA: "EDUCACIÓN PRIMARIA",
  SECUNDARIA: "EDUCACIÓN SECUNDARIA",
};
const FORM_CODE = {
  PREESCOLAR: "911.1",
  PRIMARIA: "911.2",
  SECUNDARIA: "911.4",
};
const BECA_LABELS = {
  RITA_CETINA: "Beca Rita Cetina (federal)",
  FEDERAL_OTRA: "Otra beca federal",
  ESTATAL: "Beca estatal",
  MUNICIPAL: "Beca municipal",
  FUNDACION: "Beca de fundaciones y asociaciones civiles",
  PROPIA: "Beca de la propia escuela",
  PARTICULAR: "Beca particular",
  OTRA: "Otra beca",
};
const REGION_LABEL = {
  estados_unidos: "Estados Unidos",
  canada: "Canadá",
  centroamerica_caribe: "Centroamérica y el Caribe",
  sudamerica: "Sudamérica",
  africa: "África",
  asia: "Asia",
  europa: "Europa",
  oceania: "Oceanía",
  otros: "Otros / No especificado",
};
const NIVELES_ESTUDIO_LABELS = {
  primaria_incompleta: "Primaria incompleta",
  primaria_terminada: "Primaria terminada",
  secundaria_incompleta: "Secundaria incompleta",
  secundaria_terminada: "Secundaria terminada",
  profesional_tecnico: "Profesional técnico",
  bachillerato_incompleto: "Bachillerato incompleto",
  bachillerato_terminado: "Bachillerato terminado",
  normal_preescolar_incompleta: "Normal preescolar incompleta",
  normal_preescolar_terminada: "Normal preescolar terminada",
  normal_primaria_incompleta: "Normal primaria incompleta",
  normal_primaria_terminada: "Normal primaria terminada",
  normal_superior_incompleta: "Normal superior incompleta",
  normal_superior_pasante: "Normal superior pasante",
  normal_superior_titulado: "Normal superior titulado",
  licenciatura_incompleta: "Licenciatura incompleta",
  licenciatura_pasante: "Licenciatura pasante",
  licenciatura_titulado: "Licenciatura titulado",
  maestria_incompleta: "Maestría incompleta",
  maestria_graduado: "Maestría graduado",
  doctorado_incompleto: "Doctorado incompleto",
  doctorado_graduado: "Doctorado graduado",
  otros: "Otros",
};
const PERSONAL_ROLES = [
  ["directivo_con_grupo", "Director c/grupo"],
  ["directivo_sin_grupo", "Director s/grupo"],
  ["subdirector_gestion", "Subdir. gestión"],
  ["subdirector_academico", "Subdir. académico"],
  ["docente", "Docente"],
  ["docente_educ_fisica", "Educ. física"],
  ["docente_artes", "Artísticas"],
  ["docente_idiomas", "Idiomas"],
  ["administrativo", "Administrativo"],
];
// Mapea cada columna de V.1 a los códigos SEP 911 devueltos por auto_911.personal_por_funcion.matriz.
const ROLE_TO_SEP_CODES = {
  directivo_con_grupo: ["DIRECTOR_CON_GRUPO"],
  directivo_sin_grupo: ["DIRECTOR_SIN_GRUPO"],
  subdirector_gestion: ["SUBDIRECTOR_GESTION_ESCOLAR"],
  subdirector_academico: ["SUBDIRECTOR_ACADEMICO"],
  docente: ["DOCENTE_FRENTE_GRUPO"],
  docente_educ_fisica: ["PROFESOR_EDUCACION_FISICA"],
  docente_artes: ["PROFESOR_ARTISTICAS", "PROFESOR_TECNOLOGICAS"],
  docente_idiomas: ["PROFESOR_IDIOMAS"],
  administrativo: [
    "MAESTRO_TALLER_LECTURA", "PROMOTOR_TIC", "INTENDENTE", "CONSERJE", "COCINERA",
    "AUX_COCINA", "VELADOR", "APOYO_ADMINISTRATIVO", "ADMINISTRADOR", "PREFECTO",
    "PREFECTO_NOCTURNO", "PSICOLOGO", "SECRETARIA", "MEDICO", "DENTISTA",
    "TRABAJADORA_SOCIAL", "CONTRALOR", "ALMACENISTA", "ENFERMERA", "JEFE_OFICINA",
    "ANALISTA_ADMIN", "TERAPISTA", "ESPECIALISTA_TECNICO", "JEFE_SERVICIOS", "OTROS",
  ],
};
const PERSONAL_ADMIN = [
  ["MAESTRO_TALLER_LECTURA", "Maestro de taller de lectura y escritura"],
  ["PROMOTOR_TIC", "Promotor de TIC"],
  ["INTENDENTE", "Intendente"],
  ["CONSERJE", "Conserje"],
  ["COCINERA", "Cocinera"],
  ["AUX_COCINA", "Auxiliar de cocina"],
  ["VELADOR", "Velador"],
  ["APOYO_ADMINISTRATIVO", "Apoyo administrativo"],
  ["ADMINISTRADOR", "Administrador"],
  ["PREFECTO", "Prefecto"],
  ["PREFECTO_NOCTURNO", "Prefecto nocturno"],
  ["PSICOLOGO", "Psicólogo"],
  ["SECRETARIA", "Secretaría"],
  ["MEDICO", "Médico"],
  ["DENTISTA", "Dentista"],
  ["TRABAJADORA_SOCIAL", "Trabajadora social"],
  ["CONTRALOR", "Responsable de fondos y valores / Contralor"],
  ["ALMACENISTA", "Almacenista"],
  ["ENFERMERA", "Enfermera"],
  ["JEFE_OFICINA", "Jefe de oficina"],
  ["ANALISTA_ADMIN", "Analista administrativo"],
  ["TERAPISTA", "Terapista"],
  ["ESPECIALISTA_TECNICO", "Especialista técnico"],
  ["JEFE_SERVICIOS", "Jefe de servicios y mantenimiento"],
  ["OTROS", "Otros"],
];
const GRADOS_PRE = [["1", "1°"], ["2", "2°"], ["3", "3°"], ["mas_un_grado", "Más de un grado"]];
const GRADOS_PRI = [["1","1°"],["2","2°"],["3","3°"],["4","4°"],["5","5°"],["6","6°"],["mas_un_grado","Más de un grado"]];
function gradosDeNivel(nivel) {
  if (nivel === "PRIMARIA") return GRADOS_PRI;
  return GRADOS_PRE; // preescolar y secundaria comparten 1-3
}
const CONDICIONES_TIPOS = [
  ["CEGUERA", "Ceguera"],
  ["BAJA VISIÓN", "Baja visión"],
  ["SORDERA", "Sordera"],
  ["HIPOACUSIA", "Hipoacusia"],
  ["SORDOCEGUERA", "Sordoceguera"],
  ["MOTRIZ", "Discapacidad motriz"],
  ["INTELECTUAL", "Discapacidad intelectual"],
  ["PSICOSOCIAL", "Discapacidad psicosocial"],
  ["TEA", "Trastorno del espectro autista"],
  ["MÚLTIPLE", "Discapacidad múltiple"],
  ["TDAH", "TDAH*"],
  ["APTITUDES_SOBRESALIENTES", "Aptitudes sobresalientes"],
  ["OTRAS_CONDICIONES", "Otras condiciones"],
];

export function Sep911Form({ report }) {
  const {
    plantel, ciclo, nivel, grados, edades, por_grado, total_alumnos, total_grupos,
    becas_federales, becas_otras, alumnado_no_becados,
    personal, personal_labels,
    procedencia_por_grado, procedencia_paises,
    condiciones_por_grado, alumnado_por_entidad_residencia,
    alumnado_indigena, alumnado_afromexicano, alumnado_extranjero_por_region,
    total_extranjero,
    config_911 = {},
    auto_911 = {},
  } = report;

  const cfg = config_911 || {};
  const auto = auto_911 || {};
  const autoPF = auto.personal_por_funcion || null;
  const autoDG = auto.docentes_por_grado || null;
  const autoH = auto.horas_semanales || null;
  // Prefer manual value; if manual is 0/empty and auto has a value, use auto and flag it.
  const pickAuto = (manualVal, autoVal) => {
    const m = Number(manualVal) || 0;
    const a = Number(autoVal) || 0;
    if (m > 0) return { v: m, isAuto: false };
    if (a > 0) return { v: a, isAuto: true };
    return { v: 0, isAuto: false };
  };
  const existencia = cfg.existencia_fin_ciclo_pasado || {};
  const gradosCfg = gradosDeNivel(nivel);
  const isPri = nivel === "PRIMARIA";
  const isSec = nivel === "SECUNDARIA";
  const isPriOrSec = isPri || isSec;
  const gradosNum = isPri ? ["1","2","3","4","5","6"] : ["1","2","3"];
  const promovidos = cfg.promovidos_por_grado || {};
  const prom6 = cfg.promovidos_6to_por_edad || {};
  const ingresoRep = cfg.ingreso_repetidores || {};
  const primerGradoAuto = report.primer_grado_preescolar_auto || {};
  const origenPreAuto = report.origen_preescolar_auto || {};
  const primerGrado = cfg.primer_grado_preescolar || {};
  const origenPre = cfg.origen_preescolar || {};
  const egresadosEdad = cfg.egresados_3ro_por_edad || {};
  const egresadosExtra = cfg.egresados_3ro_extra || {};
  const reprobaron = cfg.reprobaron_asignaturas || {};
  const regularizados = cfg.regularizados || {};
  const irregulares = cfg.irregulares || {};

  return (
    <div className="sep-form bg-white shadow print:shadow-none border border-[#E5E7EB] print:border-0 mx-auto"
         style={{ maxWidth: "1000px" }}>
      <div className="flex h-1.5">
        <div className="flex-1 bg-[#235B4E]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#9F2241]" />
      </div>

      <header className="bg-[#10312B] text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/60">
              Sistema Censo 911 · Tamaulipas
            </div>
            <div className="text-lg font-semibold leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {NIVEL_LABEL[nivel]} — Estadística 911
            </div>
            <div className="text-xs text-white/70 mt-1">
              Formato {FORM_CODE[nivel]} · Ciclo Escolar {ciclo}
            </div>
          </div>
          <div className="w-14 h-14 rounded bg-[#BC955C] flex items-center justify-center text-[#10312B] font-bold text-2xl"
               style={{ fontFamily: "'Montserrat', sans-serif" }}>
            911
          </div>
        </div>
      </header>

      <div className="px-6 py-5 space-y-6 text-[12px] text-[#111827]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

        <Section title="Datos de identificación">
          <div className="border border-[#98989A] grid grid-cols-5">
            <IdCell label="CCT" value={plantel.cct} />
            <IdCell label="Turno" value={plantel.turno} />
            <IdCell label="Sostenimiento" value={plantel.sostenimiento} />
            <IdCell label="Sector" value={plantel.sector} />
            <IdCell label="Zona escolar" value={plantel.zona_escolar} />
            <IdCell label="Nombre de la escuela" value={plantel.nombre} colSpan={5} />
            <IdCell label="Domicilio" value={plantel.domicilio} colSpan={3} />
            <IdCell label="Municipio / Alcaldía" value={plantel.municipio} />
            <IdCell label="Entidad federativa" value={plantel.entidad} />
            <IdCell label="Código postal" value={plantel.codigo_postal} />
            <IdCell label="Teléfono oficial" value={plantel.telefono} />
            <IdCell label="Celular" value={plantel.celular} />
            <IdCell label="Correo institucional" value={plantel.email_institucional} colSpan={2} />
            <IdCell label="Director(a)" value={plantel.director_nombre} colSpan={3} />
            <IdCell label="CURP" value={plantel.director_curp} />
            <IdCell label="RFC" value={plantel.director_rfc} />
          </div>
        </Section>

        <Section title="I. Existencia — fin del ciclo anterior" romanColor>
          <p className="text-[10px] text-[#6B7280] mb-1.5">
            Datos capturados manualmente (Configuración 911 → Existencia).
          </p>
          <table className="w-full border-collapse border border-[#98989A] text-[11px]">
            <thead>
              <tr className="bg-[#10312B] text-white">
                <th className="border border-[#98989A] p-1.5 text-left">Grado</th>
                <th className="border border-[#98989A] p-1">Hombres</th>
                <th className="border border-[#98989A] p-1">Mujeres</th>
                <th className="border border-[#98989A] p-1">Total</th>
                <th className="border border-[#98989A] p-1">Grupos</th>
                <th className="border border-[#98989A] p-1">L. indígena</th>
                <th className="border border-[#98989A] p-1">Fuera MX</th>
                <th className="border border-[#98989A] p-1">Afro</th>
                <th className="border border-[#98989A] p-1">Disc.</th>
                <th className="border border-[#98989A] p-1">Trast.</th>
                <th className="border border-[#98989A] p-1">Apt.</th>
                <th className="border border-[#98989A] p-1">Otras</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const totals = {H:0, M:0, T:0, grupos:0, lengua_indigena:0, nacido_fuera_mexico:0, afro:0, discapacidad:0, trastorno:0, aptitudes:0, otras:0};
                const rows = gradosCfg.map(([k, label]) => {
                  const r = existencia[k] || {};
                  const total = (r.H || 0) + (r.M || 0);
                  const isMas = k === "mas_un_grado";
                  // Sombreado para "Más de un grado" excepto columna Grupos
                  const shadedCls = isMas ? "bg-[#E5E7EB]" : "";
                  totals.H += (r.H||0); totals.M += (r.M||0); totals.T += total;
                  totals.grupos += (r.grupos||0);
                  totals.lengua_indigena += (r.lengua_indigena||0);
                  totals.nacido_fuera_mexico += (r.nacido_fuera_mexico||0);
                  totals.afro += (r.afro||0);
                  totals.discapacidad += (r.discapacidad||0);
                  totals.trastorno += (r.trastorno||0);
                  totals.aptitudes += (r.aptitudes||0);
                  totals.otras += (r.otras||0);
                  return (
                    <tr key={k} className="text-center">
                      <td className="border border-[#98989A] p-1 text-left">{label}</td>
                      <td className={`border border-[#98989A] p-1 ${shadedCls}`}>{isMas ? "—" : (r.H || 0)}</td>
                      <td className={`border border-[#98989A] p-1 ${shadedCls}`}>{isMas ? "—" : (r.M || 0)}</td>
                      <td className={`border border-[#98989A] p-1 font-semibold ${shadedCls}`}>{isMas ? "—" : total}</td>
                      <NumCell v={r.grupos} />
                      <td className={`border border-[#98989A] p-1 ${shadedCls}`}>{isMas ? "—" : (r.lengua_indigena || 0)}</td>
                      <td className={`border border-[#98989A] p-1 ${shadedCls}`}>{isMas ? "—" : (r.nacido_fuera_mexico || 0)}</td>
                      <td className={`border border-[#98989A] p-1 ${shadedCls}`}>{isMas ? "—" : (r.afro || 0)}</td>
                      <td className={`border border-[#98989A] p-1 ${shadedCls}`}>{isMas ? "—" : (r.discapacidad || 0)}</td>
                      <td className={`border border-[#98989A] p-1 ${shadedCls}`}>{isMas ? "—" : (r.trastorno || 0)}</td>
                      <td className={`border border-[#98989A] p-1 ${shadedCls}`}>{isMas ? "—" : (r.aptitudes || 0)}</td>
                      <td className={`border border-[#98989A] p-1 ${shadedCls}`}>{isMas ? "—" : (r.otras || 0)}</td>
                    </tr>
                  );
                });
                rows.push(
                  <tr key="total" className="bg-[#10312B] text-white text-center font-semibold">
                    <td className="border border-[#98989A] p-1 text-left">TOTAL</td>
                    <td className="border border-[#98989A] p-1">{totals.H}</td>
                    <td className="border border-[#98989A] p-1">{totals.M}</td>
                    <td className="border border-[#98989A] p-1">{totals.T}</td>
                    <td className="border border-[#98989A] p-1">{totals.grupos}</td>
                    <td className="border border-[#98989A] p-1">{totals.lengua_indigena}</td>
                    <td className="border border-[#98989A] p-1">{totals.nacido_fuera_mexico}</td>
                    <td className="border border-[#98989A] p-1">{totals.afro}</td>
                    <td className="border border-[#98989A] p-1">{totals.discapacidad}</td>
                    <td className="border border-[#98989A] p-1">{totals.trastorno}</td>
                    <td className="border border-[#98989A] p-1">{totals.aptitudes}</td>
                    <td className="border border-[#98989A] p-1">{totals.otras}</td>
                  </tr>
                );
                return rows;
              })()}
            </tbody>
          </table>
        </Section>

        {isPriOrSec && (
          <Section title="I.2 Alumnado promovido (ciclo anterior)" romanColor>
            <table className="w-full border-collapse border border-[#98989A] text-[11px]">
              <thead>
                <tr className="bg-[#10312B] text-white">
                  <th className="border border-[#98989A] p-1.5 text-left">Grado</th>
                  <th className="border border-[#98989A] p-1">H</th><th className="border border-[#98989A] p-1">M</th>
                  <th className="border border-[#98989A] p-1">Total</th>
                  <th className="border border-[#98989A] p-1">L. indígena</th>
                  <th className="border border-[#98989A] p-1">Fuera MX</th>
                  <th className="border border-[#98989A] p-1">Afro</th>
                  <th className="border border-[#98989A] p-1">Disc.</th>
                  <th className="border border-[#98989A] p-1">Trast.</th>
                  <th className="border border-[#98989A] p-1">Apt.</th>
                  <th className="border border-[#98989A] p-1">Otras</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const totals = {H:0, M:0, T:0, lengua_indigena:0, nacido_fuera_mexico:0, afro:0, discapacidad:0, trastorno:0, aptitudes:0, otras:0};
                  const rows = gradosNum.map((k) => {
                    const r = promovidos[k] || {};
                    const tot = (r.H || 0) + (r.M || 0);
                    totals.H += (r.H||0); totals.M += (r.M||0); totals.T += tot;
                    totals.lengua_indigena += (r.lengua_indigena||0);
                    totals.nacido_fuera_mexico += (r.nacido_fuera_mexico||0);
                    totals.afro += (r.afro||0);
                    totals.discapacidad += (r.discapacidad||0);
                    totals.trastorno += (r.trastorno||0);
                    totals.aptitudes += (r.aptitudes||0);
                    totals.otras += (r.otras||0);
                    return (
                      <tr key={k} className="text-center">
                        <td className="border border-[#98989A] p-1 text-left">{k}°</td>
                        <NumCell v={r.H} /><NumCell v={r.M} /><NumCell v={tot} strong />
                        <NumCell v={r.lengua_indigena} />
                        <NumCell v={r.nacido_fuera_mexico} />
                        <NumCell v={r.afro} />
                        <NumCell v={r.discapacidad} />
                        <NumCell v={r.trastorno} />
                        <NumCell v={r.aptitudes} />
                        <NumCell v={r.otras} />
                      </tr>
                    );
                  });
                  rows.push(
                    <tr key="total" className="bg-[#10312B] text-white text-center font-semibold">
                      <td className="border border-[#98989A] p-1 text-left">TOTAL</td>
                      <td className="border border-[#98989A] p-1">{totals.H}</td>
                      <td className="border border-[#98989A] p-1">{totals.M}</td>
                      <td className="border border-[#98989A] p-1">{totals.T}</td>
                      <td className="border border-[#98989A] p-1">{totals.lengua_indigena}</td>
                      <td className="border border-[#98989A] p-1">{totals.nacido_fuera_mexico}</td>
                      <td className="border border-[#98989A] p-1">{totals.afro}</td>
                      <td className="border border-[#98989A] p-1">{totals.discapacidad}</td>
                      <td className="border border-[#98989A] p-1">{totals.trastorno}</td>
                      <td className="border border-[#98989A] p-1">{totals.aptitudes}</td>
                      <td className="border border-[#98989A] p-1">{totals.otras}</td>
                    </tr>
                  );
                  return rows;
                })()}
              </tbody>
            </table>
            {isPri && (
              <>
                <div className="text-[10px] text-[#6B7280] mt-3 mb-1">I.3 Promovidos de 6° por edad</div>
                <table className="w-full border-collapse border border-[#98989A] text-[11px] max-w-3xl">
                  <thead>
                    <tr className="bg-[#10312B] text-white">
                      <th className="border border-[#98989A] p-1.5 text-left">Sexo</th>
                      {["10","11","12","13","14","15"].map((e) => (
                        <th key={e} className="border border-[#98989A] p-1">{e === "15" ? "15+" : e}</th>
                      ))}
                      <th className="border border-[#98989A] p-1">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["H","M"].map((gen) => {
                      let t = 0;
                      return (
                        <tr key={gen} className="text-center">
                          <td className="border border-[#98989A] p-1 text-left">{gen === "H" ? "Hombres" : "Mujeres"}</td>
                          {["10","11","12","13","14","15"].map((e) => {
                            const v = (prom6[e] || {})[gen] || 0;
                            t += v;
                            return <NumCell key={e} v={v} />;
                          })}
                          <NumCell v={t} strong />
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </Section>
        )}

        <Section title="II. Alumnado que proviene de otra escuela" romanColor>
          <table className="w-full border-collapse border border-[#98989A] text-[11px]">
            <thead>
              <tr className="bg-[#10312B] text-white">
                <th rowSpan={2} className="border border-[#98989A] p-1.5 text-left">Grado</th>
                <th colSpan={2} className="border border-[#98989A] p-1">Misma entidad</th>
                <th colSpan={2} className="border border-[#98989A] p-1">Otra entidad</th>
                <th colSpan={2} className="border border-[#98989A] p-1">Otro país</th>
                <th colSpan={2} className="border border-[#98989A] p-1">Total</th>
              </tr>
              <tr className="bg-[#235B4E] text-white">
                {["H","M","H","M","H","M","H","M"].map((x, i) => (
                  <th key={i} className="border border-[#98989A] p-0.5">{x}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                const totals = { me:{H:0,M:0}, oe:{H:0,M:0}, op:{H:0,M:0}, tot:{H:0,M:0} };
                const rows = grados.map((g) => {
                  const r = (procedencia_por_grado && procedencia_por_grado[g]) || (procedencia_por_grado && procedencia_por_grado[String(g)]) || {};
                  const me = r.MISMA_ENTIDAD || {H:0,M:0};
                  const oe = r.OTRA_ENTIDAD || {H:0,M:0};
                  const op = r.OTRO_PAIS || {H:0,M:0};
                  const tot = r.total || {H:0,M:0};
                  totals.me.H += me.H||0; totals.me.M += me.M||0;
                  totals.oe.H += oe.H||0; totals.oe.M += oe.M||0;
                  totals.op.H += op.H||0; totals.op.M += op.M||0;
                  totals.tot.H += tot.H||0; totals.tot.M += tot.M||0;
                  return (
                    <tr key={g} className="text-center">
                      <td className="border border-[#98989A] p-1 text-left">{g}°</td>
                      <NumCell v={me.H} /><NumCell v={me.M} />
                      <NumCell v={oe.H} /><NumCell v={oe.M} />
                      <NumCell v={op.H} /><NumCell v={op.M} />
                      <NumCell v={tot.H} strong /><NumCell v={tot.M} strong />
                    </tr>
                  );
                });
                rows.push(
                  <tr key="total" className="bg-[#10312B] text-white text-center font-semibold">
                    <td className="border border-[#98989A] p-1 text-left">TOTAL</td>
                    <td className="border border-[#98989A] p-1">{totals.me.H}</td>
                    <td className="border border-[#98989A] p-1">{totals.me.M}</td>
                    <td className="border border-[#98989A] p-1">{totals.oe.H}</td>
                    <td className="border border-[#98989A] p-1">{totals.oe.M}</td>
                    <td className="border border-[#98989A] p-1">{totals.op.H}</td>
                    <td className="border border-[#98989A] p-1">{totals.op.M}</td>
                    <td className="border border-[#98989A] p-1">{totals.tot.H}</td>
                    <td className="border border-[#98989A] p-1">{totals.tot.M}</td>
                  </tr>
                );
                return rows;
              })()}
            </tbody>
          </table>

          <div className="mt-3 text-[10px] text-[#6B7280]">II.2 De los provenientes de otro país, desglose por lugar:</div>
          <table className="w-full border-collapse border border-[#98989A] text-[11px] mt-1">
            <thead>
              <tr className="bg-[#10312B] text-white">
                <th className="border border-[#98989A] p-1.5 text-left">País o lugar</th>
                <th className="border border-[#98989A] p-1">Hombres</th>
                <th className="border border-[#98989A] p-1">Mujeres</th>
                <th className="border border-[#98989A] p-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let tH = 0, tM = 0, tT = 0;
                const rows = Object.keys(REGION_LABEL).map((r) => {
                  const v = (procedencia_paises && procedencia_paises[r]) || {H:0,M:0,total:0};
                  tH += v.H||0; tM += v.M||0; tT += v.total||0;
                  return (
                    <tr key={r} className="text-center">
                      <td className="border border-[#98989A] p-1 text-left">{REGION_LABEL[r]}</td>
                      <NumCell v={v.H} /><NumCell v={v.M} /><NumCell v={v.total} strong />
                    </tr>
                  );
                });
                rows.push(
                  <tr key="total" className="bg-[#10312B] text-white text-center font-semibold">
                    <td className="border border-[#98989A] p-1 text-left">TOTAL</td>
                    <td className="border border-[#98989A] p-1">{tH}</td>
                    <td className="border border-[#98989A] p-1">{tM}</td>
                    <td className="border border-[#98989A] p-1">{tT}</td>
                  </tr>
                );
                return rows;
              })()}
            </tbody>
          </table>
        </Section>

        <Section title="III. Becas" romanColor>
          {/* Punto 1 — sin sufijo, según indicación (solo "III" arriba) */}
          <div className="text-[11px] text-[#374151] mb-2 leading-snug">
            <span className="font-semibold">1.</span> Escriba por sexo, el número de <span className="font-semibold">alumnas y alumnos con beca y sin beca del ciclo escolar anterior</span> e indique cuántos presentan discapacidad, cuántos son hablantes de lengua indígena y cuántos son nacidos fuera de México.
          </div>
          <div className="text-[10px] text-[#BC955C] italic mb-1">Nota: Solo becas monetarias. — Datos del ciclo pasado (se cargará posteriormente).</div>
          <table className="w-full border-collapse border border-[#98989A] text-[11px]">
            <thead>
              <tr className="bg-[#10312B] text-white">
                <th className="border border-[#98989A] p-1.5 text-left">Categoría</th>
                <th className="border border-[#98989A] p-1">Hombres</th>
                <th className="border border-[#98989A] p-1">Mujeres</th>
                <th className="border border-[#98989A] p-1">Total</th>
                <th className="border border-[#98989A] p-1">Con discapacidad</th>
                <th className="border border-[#98989A] p-1">Hablantes de lenguas indígenas</th>
                <th className="border border-[#98989A] p-1">Nacidos fuera de México</th>
              </tr>
            </thead>
            <tbody>
              {["Con Beca", "Sin Beca"].map((label) => (
                <tr key={label} className="text-center">
                  <td className="border border-[#98989A] p-1 text-left font-semibold">{label}</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                </tr>
              ))}
              <tr className="bg-[#10312B] text-white text-center font-semibold">
                <td className="border border-[#98989A] p-1 text-left">Total</td>
                <td className="border border-[#98989A] p-1">—</td>
                <td className="border border-[#98989A] p-1">—</td>
                <td className="border border-[#98989A] p-1">—</td>
                <td className="border border-[#98989A] p-1">—</td>
                <td className="border border-[#98989A] p-1">—</td>
                <td className="border border-[#98989A] p-1">—</td>
              </tr>
            </tbody>
          </table>

          {/* Punto 2 — con sufijo "III.2" */}
          <div className="text-[11px] text-[#374151] mt-5 mb-2 leading-snug">
            <span className="font-semibold">III.2</span> Escriba por sexo, el número de <span className="font-semibold">alumnas y alumnos con beca</span> reportados en la pregunta anterior, y desglóselos según <span className="font-semibold">el origen de la beca</span>.
          </div>
          <table className="w-full border-collapse border border-[#98989A] text-[11px]">
            <thead>
              <tr className="bg-[#10312B] text-white">
                <th className="border border-[#98989A] p-1.5 text-left w-1/2">Origen de la beca</th>
                <th className="border border-[#98989A] p-1">Hombres</th>
                <th className="border border-[#98989A] p-1">Mujeres</th>
                <th className="border border-[#98989A] p-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {/* Becas federales */}
              <tr className="bg-[#F3F4F6]">
                <td colSpan={4} className="border border-[#98989A] p-1 text-left font-semibold text-[#BC955C]">Becas federales</td>
              </tr>
              {[
                "Beca Rita Cetina",
                "Otra*",
              ].map((r) => (
                <tr key={`fed-${r}`} className="text-center">
                  <td className="border border-[#98989A] p-1 text-left pl-4">{r}</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                </tr>
              ))}
              <tr>
                <td colSpan={4} className="border border-[#98989A] p-1 text-left text-[10px] italic text-[#6B7280] pl-4">*Especifique: __________________________________</td>
              </tr>
              <tr className="bg-[#10312B] text-white text-center font-semibold">
                <td className="border border-[#98989A] p-1 text-left">Total becas federales</td>
                <td className="border border-[#98989A] p-1">—</td>
                <td className="border border-[#98989A] p-1">—</td>
                <td className="border border-[#98989A] p-1">—</td>
              </tr>

              {/* Otras becas */}
              <tr className="bg-[#F3F4F6]">
                <td colSpan={4} className="border border-[#98989A] p-1 text-left font-semibold text-[#BC955C]">Otras becas</td>
              </tr>
              {[
                "Beca estatal",
                "Beca de fundaciones y asociaciones civiles",
                "Beca de la propia escuela",
                "Beca particular",
                "Beca municipal",
                "Otras*",
              ].map((r) => (
                <tr key={`otr-${r}`} className="text-center">
                  <td className="border border-[#98989A] p-1 text-left pl-4">{r}</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                  <td className="border border-[#98989A] p-1 h-6 text-[#9CA3AF]">—</td>
                </tr>
              ))}
              <tr>
                <td colSpan={4} className="border border-[#98989A] p-1 text-left text-[10px] italic text-[#6B7280] pl-4">*Especifique: __________________________________</td>
              </tr>
              <tr className="bg-[#10312B] text-white text-center font-semibold">
                <td className="border border-[#98989A] p-1 text-left">Total de becas</td>
                <td className="border border-[#98989A] p-1">—</td>
                <td className="border border-[#98989A] p-1">—</td>
                <td className="border border-[#98989A] p-1">—</td>
              </tr>
            </tbody>
          </table>
          <div className="text-[10px] text-[#BC955C] italic mt-2">Nota: Si alguna alumna o alumno cuenta con más de una beca, contabilice solamente la de mayor monto.</div>
        </Section>

        <Section title="IV. Alumnado y grupos" romanColor>
          <div className="text-[10px] text-[#6B7280] mb-1"><span className="font-semibold">1.</span> Alumnos por grado, sexo, nuevo ingreso, repetidores y edad</div>
          <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-[#98989A] text-[10px]">
            <thead>
              <tr className="bg-[#10312B] text-white">
                <th rowSpan={2} className="border border-[#98989A] p-1 text-left w-14">Grado</th>
                <th rowSpan={2} className="border border-[#98989A] p-1 w-14">Sexo</th>
                <th rowSpan={2} className="border border-[#98989A] p-1 w-24 text-left">Situación</th>
                {edades.map((e, i) => (
                  <th key={e} className="border border-[#98989A] p-1">
                    {i === 0 ? `<${Number(e) + 1}` : i === edades.length - 1 ? `${e}+` : `${e}`}
                  </th>
                ))}
                <th className="border border-[#98989A] p-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const totalRow = { H: { ni: {}, rep: {} }, M: { ni: {}, rep: {} } };
                edades.forEach((e) => {
                  totalRow.H.ni[e] = 0; totalRow.H.rep[e] = 0;
                  totalRow.M.ni[e] = 0; totalRow.M.rep[e] = 0;
                });
                const rows = [];
                grados.forEach((g) => {
                  const b = por_grado[g] || por_grado[String(g)] || {};
                  const pNi = b.por_edad_ni || {};
                  const pRep = b.por_edad_rep || {};
                  const cell = (map, e, sexo) => (map[e] || map[String(e)] || {})[sexo] || 0;
                  const rowSum = (map, sexo) => edades.reduce((s, e) => s + cell(map, e, sexo), 0);
                  // Sombreado SEP: NI col < G-1, Rep col < G  (colIdx 0-based)
                  const isBlockedNI = (colIdx) => colIdx < (g - 1);
                  const isBlockedRep = (colIdx) => colIdx < g;
                  const gradoRows = [
                    { sexo: "H", situacion: "Nuevo ingreso", map: pNi, isBlocked: isBlockedNI },
                    { sexo: "H", situacion: "Repetidores", map: pRep, isBlocked: isBlockedRep },
                    { sexo: "M", situacion: "Nuevo ingreso", map: pNi, isBlocked: isBlockedNI },
                    { sexo: "M", situacion: "Repetidores", map: pRep, isBlocked: isBlockedRep },
                  ];
                  gradoRows.forEach((r, idx) => {
                    edades.forEach((e) => {
                      const v = cell(r.map, e, r.sexo);
                      const bucket = r.situacion === "Nuevo ingreso" ? "ni" : "rep";
                      totalRow[r.sexo][bucket][e] += v;
                    });
                    rows.push(
                      <tr key={`${g}-${idx}`} className="text-center">
                        {idx === 0 && (
                          <td rowSpan={5} className="border border-[#98989A] p-1 text-left font-semibold align-middle">{g}°</td>
                        )}
                        {(idx === 0 || idx === 2) && (
                          <td rowSpan={2} className="border border-[#98989A] p-1 align-middle">{r.sexo}</td>
                        )}
                        <td className="border border-[#98989A] p-1 text-left">{r.situacion}</td>
                        {edades.map((e, ci) => <NumCell key={e} v={cell(r.map, e, r.sexo)} blocked={r.isBlocked(ci)} />)}
                        <NumCell v={rowSum(r.map, r.sexo)} strong />
                      </tr>
                    );
                  });
                  // Subtotal por grado: se sombrea donde AMBOS (NI y Rep) estarían bloqueados = NI-blocked
                  const subCell = (e) => (cell(pNi, e, "H") + cell(pNi, e, "M") + cell(pRep, e, "H") + cell(pRep, e, "M"));
                  const subTotal = edades.reduce((s, e) => s + subCell(e), 0);
                  rows.push(
                    <tr key={`${g}-sub`} className="bg-[#F3F4F6] text-center font-semibold">
                      <td colSpan={2} className="border border-[#98989A] p-1 text-left">Subtotal</td>
                      {edades.map((e, ci) => <NumCell key={e} v={subCell(e)} blocked={isBlockedNI(ci)} />)}
                      <NumCell v={subTotal} strong />
                    </tr>
                  );
                });
                // Total general por sexo × situación
                const totalRows = [
                  { sexo: "H", situacion: "Nuevo ingreso", bucket: "ni" },
                  { sexo: "H", situacion: "Repetidores", bucket: "rep" },
                  { sexo: "M", situacion: "Nuevo ingreso", bucket: "ni" },
                  { sexo: "M", situacion: "Repetidores", bucket: "rep" },
                ];
                totalRows.forEach((r, idx) => {
                  const rowSum = edades.reduce((s, e) => s + (totalRow[r.sexo][r.bucket][e] || 0), 0);
                  // En Total: NI nunca bloqueado; Rep bloqueado solo colIdx 0 (edad <6)
                  const isBlocked = (ci) => r.bucket === "rep" && ci === 0;
                  rows.push(
                    <tr key={`total-${idx}`} className="bg-[#FAFAFA] text-center">
                      {idx === 0 && (
                        <td rowSpan={4} className="border border-[#98989A] p-1 text-left font-semibold align-middle">Total</td>
                      )}
                      {(idx === 0 || idx === 2) && (
                        <td rowSpan={2} className="border border-[#98989A] p-1 align-middle font-semibold">{r.sexo}</td>
                      )}
                      <td className="border border-[#98989A] p-1 text-left">{r.situacion}</td>
                      {edades.map((e, ci) => <NumCell key={e} v={totalRow[r.sexo][r.bucket][e] || 0} blocked={isBlocked(ci)} />)}
                      <NumCell v={rowSum} strong />
                    </tr>
                  );
                });
                // Fila TOTAL final (suma H+M ni+rep)
                const grand = (e) => (totalRow.H.ni[e] || 0) + (totalRow.H.rep[e] || 0) + (totalRow.M.ni[e] || 0) + (totalRow.M.rep[e] || 0);
                rows.push(
                  <tr key="grand-total" className="bg-[#10312B] text-white text-center font-semibold">
                    <td colSpan={3} className="border border-[#98989A] p-1 text-left">TOTAL</td>
                    {edades.map((e) => <td key={e} className="border border-[#98989A] p-1">{grand(e)}</td>)}
                    <td className="border border-[#98989A] p-1">{total_alumnos.total}</td>
                  </tr>
                );
                return rows;
              })()}
            </tbody>
          </table>
          </div>

          {isPri && (
            <>
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                {/* IV.2 Grupos por grado */}
                <div>
                  <div className="text-[11px] text-[#374151] mb-1 leading-snug">
                    <span className="font-semibold">IV.2</span> Escriba el número total de <span className="font-semibold">grupos por grado</span>.
                  </div>
                  <div className="text-[10px] text-[#BC955C] italic mb-2">Nota: Si en un grupo se atiende más de un grado, anótelo en el apartado MÁS DE UN GRADO.</div>
                  <table className="w-full border-collapse border border-[#98989A] text-[11px]">
                    <thead>
                      <tr className="bg-[#10312B] text-white">
                        <th className="border border-[#98989A] p-1.5 text-left w-2/3">Grado</th>
                        <th className="border border-[#98989A] p-1">Grupos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[["1","Primero"],["2","Segundo"],["3","Tercero"],["4","Cuarto"],["5","Quinto"],["6","Sexto"]].map(([g,label]) => {
                        const v = (por_grado[g] || por_grado[Number(g)] || {}).grupos || 0;
                        return (
                          <tr key={g} className="text-center">
                            <td className="border border-[#98989A] p-1 text-left">{label}</td>
                            <NumCell v={v} />
                          </tr>
                        );
                      })}
                      <tr className="text-center">
                        <td className="border border-[#98989A] p-1 text-left">Más de un grado</td>
                        <NumCell v={report.grupos_mas_un_grado || 0} />
                      </tr>
                      <tr className="bg-[#10312B] text-white text-center font-semibold">
                        <td className="border border-[#98989A] p-1 text-left">Total</td>
                        <td className="border border-[#98989A] p-1">{total_grupos}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* IV.3 Alumnos de 1° según preescolar cursado */}
                <div>
                  <div className="text-[11px] text-[#374151] mb-2 leading-snug">
                    <span className="font-semibold">IV.3</span> Del total de <span className="font-semibold">alumnas y alumnos reportados en primer grado</span>, registre la cantidad de los que cursaron educación preescolar, según los años cursados y tipo de ingreso, desglosándolos por sexo y cuántos tienen discapacidad.
                  </div>
                  <table className="w-full border-collapse border border-[#98989A] text-[11px]">
                    <thead>
                      <tr className="bg-[#10312B] text-white">
                        <th className="border border-[#98989A] p-1 text-left w-24">Años cursados</th>
                        <th className="border border-[#98989A] p-1 text-left">Tipo</th>
                        <th className="border border-[#98989A] p-1">Hombres</th>
                        <th className="border border-[#98989A] p-1">Mujeres</th>
                        <th className="border border-[#98989A] p-1">Total</th>
                        <th className="border border-[#98989A] p-1">Con Discapacidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const rows = [];
                        let tH=0, tM=0, tD=0;
                        [["1_ano","1 año cursado"],["2_anos","2 años cursados"],["3_anos","3 años cursados"]].forEach(([k, label]) => {
                          const rMan = primerGrado[k] || {};
                          const rAuto = primerGradoAuto[k] || {};
                          [["nuevo_ingreso","Nuevo ingreso"],["repetidores","Repetidores"]].forEach(([sk, sLabel], si) => {
                            const vMan = rMan[sk] || {};
                            const vAuto = rAuto[sk] || {};
                            const pick = (m, a) => (Number(m) || 0) > 0 ? Number(m) : Number(a || 0);
                            const h = pick(vMan.H, vAuto.H);
                            const m = pick(vMan.M, vAuto.M);
                            const d = pick(vMan.con_discapacidad, vAuto.con_discapacidad);
                            tH+=h; tM+=m; tD+=d;
                            rows.push(
                              <tr key={`${k}-${sk}`} className="text-center">
                                {si === 0 && (
                                  <td rowSpan={2} className="border border-[#98989A] p-1 text-left align-middle font-medium">{label}</td>
                                )}
                                <td className="border border-[#98989A] p-1 text-left">{sLabel}</td>
                                <NumCell v={h} />
                                <NumCell v={m} />
                                <NumCell v={h+m} strong />
                                <NumCell v={d} />
                              </tr>
                            );
                          });
                        });
                        rows.push(
                          <tr key="iv3-total" className="bg-[#10312B] text-white text-center font-semibold">
                            <td colSpan={2} className="border border-[#98989A] p-1 text-left">Total</td>
                            <td className="border border-[#98989A] p-1">{tH}</td>
                            <td className="border border-[#98989A] p-1">{tM}</td>
                            <td className="border border-[#98989A] p-1">{tH+tM}</td>
                            <td className="border border-[#98989A] p-1">{tD}</td>
                          </tr>
                        );
                        return rows;
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* IV.4 Alumnado hablante de lengua indígena */}
              <div className="mt-4">
                <div className="text-[11px] text-[#374151] mb-1 leading-snug">
                  <span className="font-semibold">IV.4</span> Escriba el número de <span className="font-semibold">alumnas y alumnos indígenas o hablantes de lengua indígena</span>, desglosándolo por sexo.
                </div>
                <table className="w-full max-w-md border-collapse border border-[#98989A] text-[11px]">
                  <thead>
                    <tr className="bg-[#10312B] text-white">
                      <th className="border border-[#98989A] p-1">Hombres</th>
                      <th className="border border-[#98989A] p-1">Mujeres</th>
                      <th className="border border-[#98989A] p-1">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="text-center">
                      <NumCell v={alumnado_indigena?.H || 0} />
                      <NumCell v={alumnado_indigena?.M || 0} />
                      <NumCell v={alumnado_indigena?.total || 0} strong />
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* IV.5 Alumnado nacido fuera de México */}
              <div className="mt-4">
                <div className="text-[11px] text-[#374151] mb-1 leading-snug">
                  <span className="font-semibold">IV.5</span> Escriba el número de <span className="font-semibold">alumnas y alumnos nacidos fuera de México</span>, desglosándolo por sexo.
                </div>
                <table className="w-full border-collapse border border-[#98989A] text-[11px]">
                  <thead>
                    <tr className="bg-[#10312B] text-white">
                      <th className="border border-[#98989A] p-1.5 text-left">País o lugar</th>
                      <th className="border border-[#98989A] p-1">Hombres</th>
                      <th className="border border-[#98989A] p-1">Mujeres</th>
                      <th className="border border-[#98989A] p-1">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(REGION_LABEL).filter((r) => r !== "otros").map((r) => {
                      const v = (alumnado_extranjero_por_region && alumnado_extranjero_por_region[r]) || {H:0,M:0,total:0};
                      return (
                        <tr key={r} className="text-center">
                          <td className="border border-[#98989A] p-1 text-left">{REGION_LABEL[r]}</td>
                          <NumCell v={v.H} /><NumCell v={v.M} /><NumCell v={v.total} strong />
                        </tr>
                      );
                    })}
                    <tr className="bg-[#10312B] text-white text-center font-semibold">
                      <td className="border border-[#98989A] p-1 text-left">Total</td>
                      <td className="border border-[#98989A] p-1">{total_extranjero?.H || 0}</td>
                      <td className="border border-[#98989A] p-1">{total_extranjero?.M || 0}</td>
                      <td className="border border-[#98989A] p-1">{total_extranjero?.total || 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* IV.6 USAER */}
              <div className="mt-4">
                <div className="text-[11px] text-[#374151] mb-1 leading-snug">
                  <span className="font-semibold">IV.6</span> Escriba el número total de <span className="font-semibold">alumnas y alumnos atendidos por la Unidad de Servicios de Apoyo a la Educación Regular (USAER)</span>, desglosándolos por sexo.
                </div>
                {(() => {
                  const h = pickAuto(cfg.usaer?.H, auto.usaer?.H);
                  const m = pickAuto(cfg.usaer?.M, auto.usaer?.M);
                  return (
                    <table className="w-full max-w-md border-collapse border border-[#98989A] text-[11px]">
                      <thead>
                        <tr className="bg-[#10312B] text-white">
                          <th className="border border-[#98989A] p-1">Hombres</th>
                          <th className="border border-[#98989A] p-1">Mujeres</th>
                          <th className="border border-[#98989A] p-1">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="text-center">
                          <NumCell v={h.v} />
                          <NumCell v={m.v} />
                          <NumCell v={h.v + m.v} strong />
                        </tr>
                      </tbody>
                    </table>
                  );
                })()}
              </div>

              {/* IV.7 Alumnado que procede de preescolar */}
              <div className="mt-4">
                <div className="text-[11px] text-[#374151] mb-1 leading-snug">
                  <span className="font-semibold">IV.7</span> Escriba el número de alumnas y alumnos que proceden de <span className="font-semibold">escuela preescolar general, indígena y/o comunitaria</span> desglosándolo por grado y sexo.
                </div>
              <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-[#98989A] text-[10px]">
                <thead>
                  <tr className="bg-[#10312B] text-white">
                    <th rowSpan={2} className="border border-[#98989A] p-1 text-left">Preescolar</th>
                    <th rowSpan={2} className="border border-[#98989A] p-0.5">Sexo</th>
                    {gradosNum.map((k) => (
                      <th key={k} colSpan={2} className="border border-[#98989A] p-0.5">{k}°</th>
                    ))}
                    <th colSpan={2} className="border border-[#98989A] p-0.5">Total</th>
                  </tr>
                  <tr className="bg-[#235B4E] text-white">
                    {[...gradosNum, "total"].map((k) => (
                      <Fragment key={k}>
                        <th className="border border-[#98989A] p-0.5">Nvo. Ingreso</th>
                        <th className="border border-[#98989A] p-0.5">Repetidor</th>
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[["general","P. General"],["indigena","P. Indígena"],["comunitaria","P. Comunitaria"]].map(([tipo, label]) => {
                    const tipoData = origenPreAuto[tipo] || {};
                    return ["H","M"].map((sx, si) => {
                      let sumNi = 0, sumRep = 0;
                      const cells = gradosNum.map((k) => {
                        const r = tipoData[k] || {ni:{H:0,M:0}, rep:{H:0,M:0}};
                        const ni = r.ni?.[sx] || 0;
                        const rep = r.rep?.[sx] || 0;
                        sumNi += ni; sumRep += rep;
                        return { ni, rep };
                      });
                      return (
                        <tr key={`${tipo}-${sx}`} className="text-center">
                          {si === 0 && (
                            <td rowSpan={2} className="border border-[#98989A] p-1 text-left align-middle">{label}</td>
                          )}
                          <td className="border border-[#98989A] p-0.5 font-medium">{sx === "H" ? "Hombres" : "Mujeres"}</td>
                          {cells.map((c, i) => (
                            <Fragment key={i}>
                              <NumCell v={c.ni} />
                              <NumCell v={c.rep} />
                            </Fragment>
                          ))}
                          <NumCell v={sumNi} strong />
                          <NumCell v={sumRep} strong />
                        </tr>
                      );
                    });
                  })}
                  {(() => {
                    const totCols = gradosNum.map((k) => {
                      let ni = 0, rep = 0;
                      ["general","indigena","comunitaria"].forEach((tipo) => {
                        const r = (origenPreAuto[tipo] || {})[k] || {ni:{H:0,M:0}, rep:{H:0,M:0}};
                        ni += (r.ni?.H || 0) + (r.ni?.M || 0);
                        rep += (r.rep?.H || 0) + (r.rep?.M || 0);
                      });
                      return { ni, rep };
                    });
                    const tNi = totCols.reduce((s, c) => s + c.ni, 0);
                    const tRep = totCols.reduce((s, c) => s + c.rep, 0);
                    return (
                      <tr className="bg-[#10312B] text-white text-center font-semibold">
                        <td colSpan={2} className="border border-[#98989A] p-1 text-left">TOTAL</td>
                        {totCols.map((c, i) => (
                          <Fragment key={i}>
                            <td className="border border-[#98989A] p-1">{c.ni}</td>
                            <td className="border border-[#98989A] p-1">{c.rep}</td>
                          </Fragment>
                        ))}
                        <td className="border border-[#98989A] p-1">{tNi}</td>
                        <td className="border border-[#98989A] p-1">{tRep}</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
              </div>
              </div>

              {/* IV.8 Lengua materna del alumnado de preescolar indígena */}
              <div className="mt-4">
                <div className="text-[11px] text-[#374151] mb-1 leading-snug">
                  <span className="font-semibold">IV.8</span> Escriba el <span className="font-semibold">nombre de la lengua materna</span> que hablan las alumnas y alumnos procedentes de escuela de preescolar indígena.
                </div>
                <table className="w-full max-w-2xl border-collapse border border-[#98989A] text-[11px]">
                  <thead>
                    <tr className="bg-[#10312B] text-white">
                      <th className="border border-[#98989A] p-1 w-20">Clave</th>
                      <th className="border border-[#98989A] p-1 text-left">Lengua materna</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="text-center">
                      <td className="border border-[#98989A] p-1 h-6 text-[#111827] font-mono">{cfg.lenguas_maternas_clave || <span className="text-[#9CA3AF]">—</span>}</td>
                      <td className="border border-[#98989A] p-1 text-left">{cfg.lenguas_maternas_texto || <span className="text-[#9CA3AF]">—</span>}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* IV.9 Alumnado con discapacidad, neurodivergencia u otras condiciones */}
              <div className="mt-4">
                <div className="text-[11px] text-[#374151] mb-1 leading-snug">
                  <span className="font-semibold">IV.9</span> Escriba la cantidad de <span className="font-semibold">alumnas y alumnos con discapacidades, neurodivergencia u otras condiciones</span> (no consideradas en los rubros anteriores), desglosándolos por grado y sexo.
                </div>
              <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-[#98989A] text-[10px]">
                <thead>
                  <tr className="bg-[#10312B] text-white">
                    <th rowSpan={2} className="border border-[#98989A] p-1 text-left">Condición del alumnado</th>
                    {grados.map((g) => (
                      <th key={g} colSpan={3} className="border border-[#98989A] p-1">{g}°</th>
                    ))}
                    <th colSpan={3} className="border border-[#98989A] p-1">Total</th>
                  </tr>
                  <tr className="bg-[#235B4E] text-white">
                    {grados.concat(["total"]).map((g, i) => (
                      <Fragment key={i}>
                        <th className="border border-[#98989A] p-0.5">Hombres</th>
                        <th className="border border-[#98989A] p-0.5">Mujeres</th>
                        <th className="border border-[#98989A] p-0.5 bg-[#3F7A6D]">Total</th>
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const gradosArr = grados;
                    const rows = CONDICIONES_TIPOS.map(([key, label]) => {
                      let totH = 0, totM = 0;
                      const cells = gradosArr.map((g) => {
                        const c = ((condiciones_por_grado||{})[g] || (condiciones_por_grado||{})[String(g)] || {})[key] || {H:0,M:0};
                        totH += c.H || 0; totM += c.M || 0;
                        return c;
                      });
                      return (
                        <tr key={key} className="text-center">
                          <td className="border border-[#98989A] p-1 text-left">{label}</td>
                          {cells.map((c, i) => (
                            <Fragment key={i}>
                              <NumCell v={c.H} />
                              <NumCell v={c.M} />
                              <NumCell v={(c.H||0)+(c.M||0)} light />
                            </Fragment>
                          ))}
                          <NumCell v={totH} strong />
                          <NumCell v={totM} strong />
                          <NumCell v={totH+totM} strong light />
                        </tr>
                      );
                    });
                    const colTotals = gradosArr.map((g) => {
                      let h = 0, m = 0;
                      CONDICIONES_TIPOS.forEach(([key]) => {
                        const c = ((condiciones_por_grado||{})[g] || (condiciones_por_grado||{})[String(g)] || {})[key] || {H:0,M:0};
                        h += c.H || 0; m += c.M || 0;
                      });
                      return { h, m };
                    });
                    const gH = colTotals.reduce((s, c) => s + c.h, 0);
                    const gM = colTotals.reduce((s, c) => s + c.m, 0);
                    rows.push(
                      <tr key="cond-total" className="bg-[#10312B] text-white text-center font-semibold">
                        <td className="border border-[#98989A] p-1 text-left">Total</td>
                        {colTotals.map((c, i) => (
                          <Fragment key={i}>
                            <td className="border border-[#98989A] p-1">{c.h}</td>
                            <td className="border border-[#98989A] p-1">{c.m}</td>
                            <td className="border border-[#98989A] p-1 bg-[#3F7A6D]">{c.h + c.m}</td>
                          </Fragment>
                        ))}
                        <td className="border border-[#98989A] p-1">{gH}</td>
                        <td className="border border-[#98989A] p-1">{gM}</td>
                        <td className="border border-[#98989A] p-1 bg-[#3F7A6D]">{gH + gM}</td>
                      </tr>
                    );
                    return rows;
                  })()}
                </tbody>
              </table>
              </div>
              <div className="text-[9px] text-[#6B7280] italic mt-1">*TDAH: Trastorno por déficit de atención e hiperactividad.</div>
              </div>

              {/* IV.10 Afromexicanos / afrodescendientes */}
              <div className="mt-4">
                <div className="text-[11px] text-[#374151] mb-1 leading-snug">
                  <span className="font-semibold">IV.10</span> Escriba el número de alumnas y alumnos <span className="font-semibold text-[#BC955C]">afromexicanos o afrodescendientes</span> por autoadscripción de los padres, desglosándolos por sexo.
                </div>
                <table className="w-full max-w-md border-collapse border border-[#98989A] text-[11px]">
                  <thead>
                    <tr className="bg-[#10312B] text-white">
                      <th className="border border-[#98989A] p-1">Hombres</th>
                      <th className="border border-[#98989A] p-1">Mujeres</th>
                      <th className="border border-[#98989A] p-1">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="text-center">
                      <NumCell v={alumnado_afromexicano?.H || 0} />
                      <NumCell v={alumnado_afromexicano?.M || 0} />
                      <NumCell v={alumnado_afromexicano?.total || 0} strong />
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="text-[10px] text-[#6B7280] mt-3 mb-1">IV.11 Alumnado según lugar de residencia (entidad federativa)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {(() => {
                  const entries = Object.entries(alumnado_por_entidad_residencia || {})
                    .filter(([k]) => k !== "Fuera de México");
                  const half = Math.ceil(entries.length / 2);
                  const cols = [entries.slice(0, half), entries.slice(half)];
                  return cols.map((col, ci) => (
                    <table key={ci} className="w-full border-collapse border border-[#98989A] text-[10px]">
                      <thead>
                        <tr className="bg-[#10312B] text-white">
                          <th className="border border-[#98989A] p-1 text-left">Entidad</th>
                          <th className="border border-[#98989A] p-1 w-12">H</th>
                          <th className="border border-[#98989A] p-1 w-12">M</th>
                          <th className="border border-[#98989A] p-1 w-12">T</th>
                        </tr>
                      </thead>
                      <tbody>
                        {col.map(([ent, v]) => (
                          <tr key={ent} className="text-center">
                            <td className="border border-[#98989A] p-1 text-left">{ent}</td>
                            <NumCell v={v.H || 0} />
                            <NumCell v={v.M || 0} />
                            <NumCell v={(v.H||0)+(v.M||0)} strong />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ));
                })()}
              </div>
              {(() => {
                const fuera = (alumnado_por_entidad_residencia || {})["Fuera de México"] || {H:0,M:0};
                const allEnts = Object.entries(alumnado_por_entidad_residencia || {});
                const gH = allEnts.reduce((s, [, v]) => s + (v.H || 0), 0);
                const gM = allEnts.reduce((s, [, v]) => s + (v.M || 0), 0);
                return (
                  <table className="w-full border-collapse border border-[#98989A] text-[10px] mt-2">
                    <tbody>
                      <tr className="text-center bg-[#F3F4F6] font-semibold">
                        <td className="border border-[#98989A] p-1 text-left">Fuera de México</td>
                        <td className="border border-[#98989A] p-1 w-12">{fuera.H || 0}</td>
                        <td className="border border-[#98989A] p-1 w-12">{fuera.M || 0}</td>
                        <td className="border border-[#98989A] p-1 w-12">{(fuera.H||0)+(fuera.M||0)}</td>
                      </tr>
                      <tr className="text-center bg-[#10312B] text-white font-semibold">
                        <td className="border border-[#98989A] p-1 text-left">TOTAL</td>
                        <td className="border border-[#98989A] p-1 w-12">{gH}</td>
                        <td className="border border-[#98989A] p-1 w-12">{gM}</td>
                        <td className="border border-[#98989A] p-1 w-12">{gH + gM}</td>
                      </tr>
                    </tbody>
                  </table>
                );
              })()}
            </>
          )}

          {isPriOrSec && !isPri && (
            <>
              {isPri && (
                <>
                  <div className="text-[10px] text-[#6B7280] mt-3 mb-1">IV.3 Alumnos de 1° según preescolar cursado</div>
                  <table className="w-full border-collapse border border-[#98989A] text-[11px]">
                    <thead>
                      <tr className="bg-[#10312B] text-white">
                        <th rowSpan={2} className="border border-[#98989A] p-1.5 text-left">Años preescolar</th>
                        <th colSpan={3} className="border border-[#98989A] p-1">Nuevo ingreso</th>
                        <th colSpan={3} className="border border-[#98989A] p-1">Repetidores</th>
                      </tr>
                      <tr className="bg-[#235B4E] text-white">
                        {["H","M","c/Disc.","H","M","c/Disc."].map((x, i) => (
                          <th key={i} className="border border-[#98989A] p-0.5">{x}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[["1_ano","1 año"],["2_anos","2 años"],["3_anos","3 años"]].map(([k, label]) => {
                        const rMan = primerGrado[k] || {};
                        const rAuto = primerGradoAuto[k] || {};
                        const niMan = rMan.nuevo_ingreso || {};
                        const niAuto = rAuto.nuevo_ingreso || {};
                        const repMan = rMan.repetidores || {};
                        const repAuto = rAuto.repetidores || {};
                        const pick = (m, a) => (Number(m) || 0) > 0 ? Number(m) : Number(a || 0);
                        return (
                          <tr key={k} className="text-center">
                            <td className="border border-[#98989A] p-1 text-left">{label}</td>
                            <NumCell v={pick(niMan.H, niAuto.H)} /><NumCell v={pick(niMan.M, niAuto.M)} /><NumCell v={pick(niMan.con_discapacidad, niAuto.con_discapacidad)} />
                            <NumCell v={pick(repMan.H, repAuto.H)} /><NumCell v={pick(repMan.M, repAuto.M)} /><NumCell v={pick(repMan.con_discapacidad, repAuto.con_discapacidad)} />
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}

              <div className="text-[10px] text-[#6B7280] mt-3 mb-1">
                {isSec ? "IV.3 Alumnado según origen escuela primaria" : "IV.7 Alumnado según origen preescolar"}
              </div>
              <table className="w-full border-collapse border border-[#98989A] text-[10px]">
                <thead>
                  <tr className="bg-[#10312B] text-white">
                    <th rowSpan={2} className="border border-[#98989A] p-1 text-left">{isSec ? "Primaria" : "Preescolar"}</th>
                    {gradosNum.map((k) => (
                      <th key={k} colSpan={2} className="border border-[#98989A] p-0.5">{k}°</th>
                    ))}
                    <th colSpan={2} className="border border-[#98989A] p-0.5">Total</th>
                  </tr>
                  <tr className="bg-[#235B4E] text-white">
                    {[...gradosNum, "total"].map((k) => (
                      <Fragment key={k}>
                        <th className="border border-[#98989A] p-0.5">H</th>
                        <th className="border border-[#98989A] p-0.5">M</th>
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[["general", isSec ? "Prim. General" : "P. General"],["indigena", isSec ? "Prim. Indígena" : "P. Indígena"],["comunitaria", isSec ? "Prim. Comunitaria" : "P. Comunitaria"]].map(([tipo, label]) => {
                    let tH = 0, tM = 0;
                    return (
                      <tr key={tipo} className="text-center">
                        <td className="border border-[#98989A] p-1 text-left">{label}</td>
                        {gradosNum.map((k) => {
                          const vMan = ((origenPre[tipo] || {})[k]) || {};
                          const vAuto = ((origenPreAuto[tipo] || {})[k]) || {};
                          const pick = (m, a) => (Number(m) || 0) > 0 ? Number(m) : Number(a || 0);
                          const h = pick(vMan.H, vAuto.H);
                          const m = pick(vMan.M, vAuto.M);
                          tH += h; tM += m;
                          return (
                            <Fragment key={k}>
                              <NumCell v={h} />
                              <NumCell v={m} />
                            </Fragment>
                          );
                        })}
                        <NumCell v={tH} strong /><NumCell v={tM} strong />
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {cfg.lenguas_maternas_texto && (
                <div className="mt-3">
                  <div className="text-[10px] text-[#6B7280] mb-1">
                    {isSec ? "IV.4 Lenguas maternas (primaria indígena)" : "IV.8 Lenguas maternas (preescolar indígena)"}
                  </div>
                  <div className="p-2 border border-[#98989A] bg-[#F9FAFB] text-[11px]">
                    {cfg.lenguas_maternas_texto}
                  </div>
                </div>
              )}
            </>
          )}

          {isSec && (
            <>
              <div className="text-[10px] text-[#6B7280] mt-3 mb-1">IV.8 Egresados de 3° durante el ciclo anterior</div>
              <table className="w-full border-collapse border border-[#98989A] text-[10px]">
                <thead>
                  <tr className="bg-[#10312B] text-white">
                    <th className="border border-[#98989A] p-1 text-left">Sexo</th>
                    {["14","15","16","17","18"].map((e) => (
                      <th key={e} className="border border-[#98989A] p-1">{e === "18" ? "18+" : e}</th>
                    ))}
                    <th className="border border-[#98989A] p-1">L. indíg.</th>
                    <th className="border border-[#98989A] p-1">Fuera MX</th>
                    <th className="border border-[#98989A] p-1">Disc.</th>
                    <th className="border border-[#98989A] p-1">Trast.</th>
                    <th className="border border-[#98989A] p-1">Otras</th>
                  </tr>
                </thead>
                <tbody>
                  {["H","M"].map((gen) => {
                    const ex = egresadosExtra[gen] || {};
                    return (
                      <tr key={gen} className="text-center">
                        <td className="border border-[#98989A] p-1 text-left">{gen === "H" ? "Hombres" : "Mujeres"}</td>
                        {["14","15","16","17","18"].map((e) => (
                          <NumCell key={e} v={(egresadosEdad[e] || {})[gen]} />
                        ))}
                        <NumCell v={ex.lengua_indigena} />
                        <NumCell v={ex.nacido_fuera_mexico} />
                        <NumCell v={ex.discapacidad} />
                        <NumCell v={ex.trastorno} />
                        <NumCell v={ex.otras} />
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="grid md:grid-cols-3 gap-3 mt-3">
                <MiniPerGrade titulo="IV.9 Reprobaron ≥1 asignatura" data={reprobaron} autoData={auto.reprobaron_asignaturas} grados={["1","2","3"]} />
                <MiniPerGrade titulo="IV.10 Regularizados" data={regularizados} autoData={auto.regularizados} grados={["1","2","3"]} />
                <MiniPerGrade titulo="IV.11 Irregulares (continúan)" data={irregulares} autoData={auto.irregulares} grados={["2","3"]} />
              </div>

              {cfg.es_telesecundaria && (
                <>
                  <div className="text-[10px] text-[#6B7280] mt-3 mb-1">IV.12 Directivos con grupo y docentes — TELESECUNDARIA</div>
                  <table className="w-full border-collapse border border-[#98989A] text-[11px] max-w-xl">
                    <thead>
                      <tr className="bg-[#10312B] text-white">
                        <th className="border border-[#98989A] p-1 text-left">Grado</th>
                        <th className="border border-[#98989A] p-1">Directivos c/ grupo</th>
                        <th className="border border-[#98989A] p-1">Docentes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[["1","1°"],["2","2°"],["3","3°"],["mas_un_grado","Más de un grado"]].map(([k, label]) => {
                        const r = (cfg.telesecundaria_staff || {})[k] || {};
                        return (
                          <tr key={k} className="text-center">
                            <td className="border border-[#98989A] p-1 text-left">{label}</td>
                            <NumCell v={r.directivos} />
                            <NumCell v={r.docentes} />
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </Section>

        <Section title="V. Personal por función" romanColor>
          <div className="text-[10px] text-[#6B7280] mb-1">V.1 Personal por nivel de estudios y función</div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-[#98989A] text-[9px]">
              <thead>
                <tr className="bg-[#10312B] text-white">
                  <th rowSpan={2} className="border border-[#98989A] p-1 text-left min-w-[110px]">Nivel de estudios</th>
                  {PERSONAL_ROLES.map(([k, label]) => (
                    <th key={k} colSpan={2} className="border border-[#98989A] p-0.5">{label}</th>
                  ))}
                </tr>
                <tr className="bg-[#235B4E] text-white">
                  {PERSONAL_ROLES.map(([k]) => (
                    <Fragment key={k}>
                      <th className="border border-[#98989A] p-0.5">H</th>
                      <th className="border border-[#98989A] p-0.5">M</th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(NIVELES_ESTUDIO_LABELS).map(([nivelLow, label]) => {
                  const nivelSep = nivelLow.toUpperCase();
                  const manualRow = (cfg.personal_por_nivel_estudios || {})[nivelLow] || {};
                  const autoMatriz = autoPF?.matriz || {};
                  return (
                    <tr key={nivelLow} className="text-center">
                      <td className="border border-[#98989A] p-0.5 text-left">{label}</td>
                      {PERSONAL_ROLES.map(([r]) => {
                        // Suma auto-computado (todos los códigos SEP mapeados a esta columna, en este nivel)
                        let h = 0, m = 0;
                        for (const code of (ROLE_TO_SEP_CODES[r] || [])) {
                          const cell = autoMatriz[code]?.[nivelSep];
                          if (cell) { h += (cell.H || 0); m += (cell.M || 0); }
                        }
                        // Fallback a valores manuales si no hay auto
                        const manual = manualRow[r] || {H:0,M:0};
                        if (h === 0 && m === 0) { h = manual.H || 0; m = manual.M || 0; }
                        return (
                          <Fragment key={r}>
                            <NumCell v={h} />
                            <NumCell v={m} />
                          </Fragment>
                        );
                      })}
                    </tr>
                  );
                })}
                {(() => {
                  // Subtotales por función (suma vertical de todos los niveles) + TOTAL DE PERSONAL
                  const autoMatriz = autoPF?.matriz || {};
                  const nivelKeys = Object.keys(NIVELES_ESTUDIO_LABELS);
                  const subs = PERSONAL_ROLES.map(([r]) => {
                    let H = 0, M = 0;
                    for (const nivelLow of nivelKeys) {
                      const nivelSep = nivelLow.toUpperCase();
                      const manualRow = (cfg.personal_por_nivel_estudios || {})[nivelLow] || {};
                      let h = 0, m = 0;
                      for (const code of (ROLE_TO_SEP_CODES[r] || [])) {
                        const cell = autoMatriz[code]?.[nivelSep];
                        if (cell) { h += (cell.H || 0); m += (cell.M || 0); }
                      }
                      const manual = manualRow[r] || {H:0,M:0};
                      if (h === 0 && m === 0) { h = manual.H || 0; m = manual.M || 0; }
                      H += h; M += m;
                    }
                    return [r, H, M];
                  });
                  const totalGeneral = subs.reduce((s, [, H, M]) => s + H + M, 0);
                  return (
                    <>
                      <tr className="text-center bg-[#E5E7EB] font-semibold">
                        <td className="border border-[#98989A] p-0.5 text-left uppercase tracking-wide">Subtotales</td>
                        {subs.map(([r, H, M]) => (
                          <Fragment key={r}>
                            <NumCell v={H} strong />
                            <NumCell v={M} strong />
                          </Fragment>
                        ))}
                      </tr>
                      <tr className="text-center bg-[#10312B] text-white font-semibold">
                        <td className="border border-[#98989A] p-0.5" colSpan={16}></td>
                        <td className="border border-[#98989A] p-1 text-right pr-2 uppercase tracking-wide" colSpan={2}>Total de personal</td>
                        <td className="border border-[#98989A] p-1 text-center font-bold">{totalGeneral}</td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>

          <div className="text-[10px] text-[#6B7280] mt-3 mb-1">
            V.2 Personal administrativo, auxiliar y de servicios
          </div>
          <table className="w-full border-collapse border border-[#98989A] text-[11px] max-w-2xl">
            <thead>
              <tr className="bg-[#10312B] text-white">
                <th className="border border-[#98989A] p-1.5 text-left">Función</th>
                <th className="border border-[#98989A] p-1">H</th>
                <th className="border border-[#98989A] p-1">M</th>
                <th className="border border-[#98989A] p-1 bg-[#0d2823]">Total</th>
              </tr>
            </thead>
            <tbody>
              {PERSONAL_ADMIN.map(([k, label]) => {
                // Auto: usar subtotales_por_funcion del SEP code (mismo key que PERSONAL_ADMIN).
                const auto = autoPF?.subtotales_por_funcion?.[k];
                const manual = (cfg.personal_admin_detalle || {})[k];
                let H = 0, M = 0;
                if (auto) { H = auto.H || 0; M = auto.M || 0; }
                else if (manual) { H = manual.H || 0; M = manual.M || 0; }
                return (
                  <tr key={k} className="text-center">
                    <td className="border border-[#98989A] p-1 text-left">{label}</td>
                    <NumCell v={H} /><NumCell v={M} />
                    <td className="border border-[#98989A] p-1 bg-[#F3F4F6] font-semibold">{H + M}</td>
                  </tr>
                );
              })}
              {(() => {
                let TH = 0, TM = 0;
                PERSONAL_ADMIN.forEach(([k]) => {
                  const auto = autoPF?.subtotales_por_funcion?.[k];
                  const manual = (cfg.personal_admin_detalle || {})[k];
                  let H = 0, M = 0;
                  if (auto) { H = auto.H || 0; M = auto.M || 0; }
                  else if (manual) { H = manual.H || 0; M = manual.M || 0; }
                  TH += H; TM += M;
                });
                return (
                  <tr className="text-center bg-[#10312B] text-white font-semibold">
                    <td className="border border-[#98989A] p-1 text-left uppercase tracking-wide">Totales</td>
                    <td className="border border-[#98989A] p-1">{TH}</td>
                    <td className="border border-[#98989A] p-1">{TM}</td>
                    <td className="border border-[#98989A] p-1">{TH + TM}</td>
                  </tr>
                );
              })()}
            </tbody>
          </table>

          <div className="mt-4">
            <div className="text-[10px] text-[#6B7280] mb-1">V.3 Personal directivo con grupo más docentes, por grado</div>
            <p className="text-[10px] text-[#374151] mb-2">
              <span className="font-semibold">Importante:</span> Si un profesor atiende más de un grado, anótelo en el rubro &quot;Más de un grado&quot;;
              el total debe coincidir con la suma de directivo con grupo más personal docente de V.1.
            </p>
            <div className="grid grid-cols-2 gap-4 max-w-2xl">
              {(() => {
                const totalDoc = ((autoDG && autoDG.total) != null)
                  ? autoDG.total
                  : gradosCfg.reduce((s, [k]) => s + ((cfg.docentes_con_grupo_por_grado || {})[k] || 0), 0);
                const half = Math.ceil(gradosCfg.length / 2);
                const col1 = gradosCfg.slice(0, half);
                const col2 = gradosCfg.slice(half);
                const cellValue = (k) => {
                  const kUpper = k.toUpperCase();
                  if (autoDG && autoDG[k] != null) return autoDG[k];
                  if (autoDG && autoDG[kUpper] != null) return autoDG[kUpper];
                  return (cfg.docentes_con_grupo_por_grado || {})[k] || 0;
                };
                const renderRow = ([k, label]) => (
                  <tr key={k} className="text-center">
                    <td className="border border-[#98989A] p-1 text-left">{label}</td>
                    <NumCell v={cellValue(k)} />
                  </tr>
                );
                return (
                  <>
                    <table className="w-full border-collapse border border-[#98989A] text-[11px]">
                      <thead>
                        <tr className="bg-[#10312B] text-white">
                          <th className="border border-[#98989A] p-1 text-left">Grado</th>
                          <th className="border border-[#98989A] p-1">Docentes</th>
                        </tr>
                      </thead>
                      <tbody>{col1.map(renderRow)}</tbody>
                    </table>
                    <table className="w-full border-collapse border border-[#98989A] text-[11px]">
                      <thead>
                        <tr className="bg-[#10312B] text-white">
                          <th className="border border-[#98989A] p-1 text-left">Grado</th>
                          <th className="border border-[#98989A] p-1">Docentes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {col2.map(renderRow)}
                        <tr className="bg-[#10312B] text-white font-semibold text-center">
                          <td className="border border-[#98989A] p-1 text-left uppercase tracking-wide">Total</td>
                          <td className="border border-[#98989A] p-1">{totalDoc}</td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                );
              })()}
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[10px] text-[#6B7280] mb-1">V.4 Horas semanales impartidas por el personal docente especial</div>
            <table className="w-full border-collapse border border-[#98989A] text-[11px] max-w-2xl">
              <thead>
                <tr className="bg-[#10312B] text-white">
                  <th className="border border-[#98989A] p-1.5 text-left">Personal docente especial</th>
                  <th className="border border-[#98989A] p-1 w-24">Horas</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Profesores de educación física", (autoH?.educ_fisica ?? cfg.horas_semanales?.educ_fisica) || 0],
                  ["Profesores de música, acompañamiento musical, danza, etcétera (actividades artísticas)", (autoH?.artisticas ?? cfg.horas_semanales?.artisticas) || 0],
                  ["Profesores de actividades tecnológicas", (autoH?.tecnologicas ?? cfg.horas_semanales?.tecnologicas) || 0],
                  ["Profesores de idiomas", (autoH?.idiomas ?? cfg.horas_semanales?.idiomas) || 0],
                ].map(([label, val], i) => (
                  <tr key={i} className="text-center">
                    <td className="border border-[#98989A] p-1 text-left">{label}</td>
                    <NumCell v={val} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <div className="text-[10px] text-[#6B7280] mb-1">V.5 Docentes con discapacidad</div>
            <table className="w-full border-collapse border border-[#98989A] text-[11px] max-w-md">
              <thead>
                <tr className="bg-[#10312B] text-white text-center">
                  <th className="border border-[#98989A] p-1">Hombres</th>
                  <th className="border border-[#98989A] p-1">Mujeres</th>
                  <th className="border border-[#98989A] p-1 bg-[#0d2823]">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr className="text-center">
                  <NumCell v={cfg.docentes_con_discapacidad?.H || 0} />
                  <NumCell v={cfg.docentes_con_discapacidad?.M || 0} />
                  <td className="border border-[#98989A] p-1 bg-[#F3F4F6] font-semibold">{(cfg.docentes_con_discapacidad?.H || 0) + (cfg.docentes_con_discapacidad?.M || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {autoPF && autoPF.total_general?.total > 0 && (
            <p className="text-[9px] text-[#6B7280] mt-2 italic">
              V.1 se llena automáticamente con los registros de <strong>Personal por Función</strong> + <strong>docentes</strong> + <strong>Director(a)</strong> con nivel educativo y sexo capturados.
              Total general: <strong>{autoPF.total_general?.total || 0}</strong> ({autoPF.total_general?.H || 0} H · {autoPF.total_general?.M || 0} M).
            </p>
          )}
        </Section>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          .sep-form { box-shadow: none !important; border: 0 !important; }
          .sep-form section { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

function Section({ title, children, romanColor }) {
  return (
    <section>
      <div className="mb-2 pb-1 border-b-2 border-[#235B4E]">
        <div className="text-sm font-semibold uppercase tracking-wider text-[#10312B]"
             style={{ fontFamily: "'Montserrat', sans-serif" }}>
          {romanColor && title.match(/^[IVX]+\./) ? (
            <>
              <span className="text-[#9F2241] mr-1">{title.match(/^[IVX]+\./)[0]}</span>
              {title.replace(/^[IVX]+\.\s*/, "")}
            </>
          ) : title}
        </div>
      </div>
      {children}
    </section>
  );
}

function IdCell({ label, value, colSpan = 1 }) {
  return (
    <div
      className="border border-[#98989A] p-1.5"
      style={{ gridColumn: `span ${colSpan} / span ${colSpan}` }}
    >
      <div className="text-[9px] uppercase tracking-wider text-[#6B7280]">{label}</div>
      <div className="text-[12px] font-medium text-[#111827] mt-0.5">{value || "—"}</div>
    </div>
  );
}

function NumCell({ v, strong, auto, blocked, light }) {
  if (blocked) {
    return (
      <td className="border border-[#98989A] p-1 text-center bg-[#C7CCD1]" aria-hidden="true">
        <span className="sr-only">área no utilizable</span>
      </td>
    );
  }
  return (
    <td className={`border border-[#98989A] p-1 text-center ${strong ? "font-semibold text-[#10312B]" : ""} ${light ? "bg-[#E5E7EB]" : ""}`}>
      <span className="inline-flex items-center gap-1 justify-center">
        <span>{v || 0}</span>
        {auto && (
          <span
            title="Valor auto-calculado desde alumnos"
            className="text-[8px] leading-none px-1 py-[1px] rounded bg-[#BC955C]/15 text-[#7C5A1F] border border-[#BC955C]/30"
          >
            AUTO
          </span>
        )}
      </span>
    </td>
  );
}

function MiniTable({ titulo, rows, totalRow }) {
  return (
    <div>
      <div className="text-[10px] text-[#6B7280] mb-1">{titulo}</div>
      <table className="w-full border-collapse border border-[#98989A] text-[11px]">
        <tbody>
          {rows.map(([label, v, isAuto], i) => (
            <tr key={i} className="text-center">
              <td className="border border-[#98989A] p-1 text-left">{label}</td>
              <NumCell v={v} auto={isAuto} />
            </tr>
          ))}
          {totalRow && (
            <tr className="bg-[#F3F4F6] font-semibold text-center">
              <td className="border border-[#98989A] p-1 text-left">{totalRow[0]}</td>
              <NumCell v={totalRow[1]} strong />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function BecasTable({ titulo, data, labels }) {
  const porTipo = data?.por_tipo || {};
  const total = data?.total || {H:0,M:0,total:0};
  return (
    <div>
      <div className="text-[10px] text-[#6B7280] mb-1">{titulo}</div>
      <table className="w-full border-collapse border border-[#98989A] text-[11px]">
        <thead>
          <tr className="bg-[#10312B] text-white">
            <th className="border border-[#98989A] p-1 text-left">Tipo</th>
            <th className="border border-[#98989A] p-0.5">H</th>
            <th className="border border-[#98989A] p-0.5">M</th>
            <th className="border border-[#98989A] p-0.5">T</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(porTipo).length === 0 ? (
            <tr><td colSpan={4} className="border border-[#98989A] p-2 text-center text-[#6B7280]">Sin registros</td></tr>
          ) : Object.entries(porTipo).map(([tipo, b]) => (
            <tr key={tipo} className="text-center">
              <td className="border border-[#98989A] p-1 text-left">{labels[tipo] || tipo}</td>
              <NumCell v={b.H} /><NumCell v={b.M} /><NumCell v={b.total} strong />
            </tr>
          ))}
          <tr className="bg-[#10312B] text-white text-center font-semibold">
            <td className="border border-[#98989A] p-1 text-left">TOTAL</td>
            <td className="border border-[#98989A] p-1">{total.H || 0}</td>
            <td className="border border-[#98989A] p-1">{total.M || 0}</td>
            <td className="border border-[#98989A] p-1">{total.total || 0}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function MiniPerGrade({ titulo, data, autoData, grados }) {
  return (
    <div>
      <div className="text-[10px] text-[#6B7280] mb-1">{titulo}</div>
      <table className="w-full border-collapse border border-[#98989A] text-[11px]">
        <thead>
          <tr className="bg-[#10312B] text-white">
            <th className="border border-[#98989A] p-1 text-left">Grado</th>
            <th className="border border-[#98989A] p-1">H</th>
            <th className="border border-[#98989A] p-1">M</th>
          </tr>
        </thead>
        <tbody>
          {grados.map((k) => {
            const r = data?.[k] || {};
            const ar = autoData?.[k] || {};
            const mH = Number(r.H) || 0, aH = Number(ar.H) || 0;
            const mM = Number(r.M) || 0, aM = Number(ar.M) || 0;
            const useH = mH === 0 && aH > 0;
            const useM = mM === 0 && aM > 0;
            return (
              <tr key={k} className="text-center">
                <td className="border border-[#98989A] p-1 text-left">{k}°</td>
                <NumCell v={useH ? aH : mH} auto={useH} />
                <NumCell v={useM ? aM : mM} auto={useM} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SignBlock({ label, name }) {
  return (
    <div>
      <div className="border-t border-[#111827] pt-1 min-h-[3rem]" />
      <div className="text-[10px] text-[#374151]">{label}</div>
      {name && <div className="text-[11px] font-medium">{name}</div>}
    </div>
  );
}
